import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getRoleEntitlementLog} from 'src/openapi/admin/apis/EntitlementLogApi';
import {useAppDispatch} from 'src/store/hooks';

import {entitlementRoleChangeLogStateSelector} from '../slices/entitlement-role-change-log-state.slice';
import {
    entitlementRoleChangeLogActions,
    entitlementRoleChangeLogDataSelector,
    entitlementRoleChangeLogResultSelector,
} from '../slices/entitlement-role-change-log.slice';

import type {EntitlementRoleLogsCriteria} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementLogsPage} from 'src/openapi/admin/models/EntitlementLogsPage';


type GetEntitlementRoleLogCacheKeys = CacheKeys<undefined, undefined, EntitlementRoleLogsCriteria>;

type Props = {
    roleId: string,
};

export const useGetEntitlementRoleChangeLog = ({roleId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetEntitlementRoleLogCacheKeys) => apiDispatch(
        getRoleEntitlementLog({
            roleId,
            roleEntitlementLogsPageRequest: {
                pagination: {
                    pageSize: cacheKeys.pagination.pageSize,
                    currentPage: cacheKeys.pagination.currentPage,
                },
                actionName: cacheKeys.filters?.actionName as string | undefined,
                startDate: cacheKeys.filters?.startDate as string | undefined,
                endDate: cacheKeys.filters?.endDate as string | undefined,
                entitlementName: cacheKeys.filters?.entitlementName as string | undefined,
            },
        }),
    ), [roleId, apiDispatch]);

    const setLoadedData = useCallback((
        response: EntitlementLogsPage,
        cacheKeys: GetEntitlementRoleLogCacheKeys,
    ) => {
        dispatch(entitlementRoleChangeLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: entitlementRoleChangeLogDataSelector,
        stateSelector: entitlementRoleChangeLogStateSelector,
        resultSelector: entitlementRoleChangeLogResultSelector,
        actions: entitlementRoleChangeLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
